import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { createRole, updateRole } from '../../services/iam';
import { Role } from '../../types/iam';
import { toast } from '@/lib/toast';
import { Button, Checkbox, Input, Modal, Textarea } from '@/components/ds-zta';
import { apiErrorMessage, descriptionField, keyFromName, KEY_PATTERN, KEY_PATTERN_MESSAGE } from './iamForm';

interface Props {
  /** The role to edit; omit to create one. */
  role?: Role;
  onClose: () => void;
}

/* Create and edit one role. On create the key is suggested from the name until
   the user types one; on edit an unchanged existing key is left alone, even if
   it predates the key rule. */
const RoleFormModal: React.FC<Props> = ({ role, onClose }) => {
  const isEdit = !!role;
  const queryClient = useQueryClient();
  const { register, handleSubmit, setValue, formState: { errors } } = useForm<Partial<Role>>({
    defaultValues: role
      ? {
          name: role.name,
          key: role.key,
          description: role.description,
          is_primary: role.is_primary,
          is_admin: role.is_admin,
          direct_access: role.direct_access,
        }
      : undefined,
  });
  const [keyTouched, setKeyTouched] = useState(false);

  const mutation = useMutation(
    (data: Partial<Role>) => (role ? updateRole(role.id, data) : createRole(data)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('roles');
        toast.success(isEdit ? 'Role updated successfully' : 'Role created successfully');
        onClose();
      },
      onError: (err: unknown) => {
        toast.error(`Failed to ${isEdit ? 'update' : 'create'} role: ${apiErrorMessage(err)}`);
      },
    },
  );

  const onSubmit = (data: Partial<Role>) => {
    const { description: rawDescription, ...rest } = data;
    mutation.mutate({
      ...rest,
      name: data.name?.trim(),
      key: data.key?.trim(),
      ...descriptionField(rawDescription, role?.description),
    });
  };

  const nameField = register('name', { required: 'Name is required' });
  const keyField = register('key', {
    required: 'Key is required',
    validate: (v) => (isEdit && v === role.key) || KEY_PATTERN.test(v ?? '') || KEY_PATTERN_MESSAGE,
  });

  // ds Checkbox reports (checked, event); react-hook-form's onChange wants the event.
  const checkbox = (name: 'is_primary' | 'is_admin' | 'direct_access') => {
    const field = register(name);
    return { ...field, onChange: (_checked: boolean, e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e) };
  };

  const formId = isEdit ? 'edit-role-form' : 'create-role-form';

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit Role' : 'Create New Role'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} loading={mutation.isLoading}>
            {isEdit ? 'Save Changes' : 'Create Role'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            {...nameField}
            onChange={(e) => {
              nameField.onChange(e);
              if (!isEdit && !keyTouched) {
                setValue('key', keyFromName(e.target.value), { shouldValidate: !!errors.key });
              }
            }}
            id="name"
            label="Name"
            error={errors.name?.message}
          />
          <Input
            {...keyField}
            onChange={(e) => {
              setKeyTouched(e.target.value !== '');
              keyField.onChange(e);
            }}
            id="key"
            label="Key"
            help={isEdit ? undefined : 'From the name: a–z, 0–9, - _'}
            error={errors.key?.message}
          />
        </div>
        <Textarea {...register('description')} id="description" label="Description" rows={2} placeholder="What this role is for (optional)" />
        <fieldset className="rounded-[var(--radius-card)] border border-[var(--d2d-border)]">
          <legend className="ml-3 px-1 text-sm font-medium text-[var(--d2d-text-primary)]">Role type</legend>
          <div className="divide-y divide-[var(--d2d-border)]">
            <div className="px-4 py-3">
              <Checkbox
                {...checkbox('is_primary')}
                id="is_primary"
                label="Primary Role"
                description="Primary roles can be set as a user's primary role"
              />
            </div>
            <div className="px-4 py-3">
              <Checkbox
                {...checkbox('is_admin')}
                id="is_admin"
                label="Admin Role"
                description="Admin roles have elevated privileges"
              />
            </div>
            <div className="px-4 py-3">
              <Checkbox
                {...checkbox('direct_access')}
                id="direct_access"
                label="Grants Direct Access"
                description="Users can access bound resources without an approval request"
              />
            </div>
          </div>
        </fieldset>
      </form>
    </Modal>
  );
};

export default RoleFormModal;
